import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { RigsService } from 'src/app/services/rigs.service';
import { Rig } from '@models/rig.interface';
import { SaveRigDto } from '@models/save-rig-dto.interface';
import { LookupDto } from '@models/lookup-dto.interface';

/** Emitted when a save completes so the parent can refresh and re-select. */
export interface RigSavedEvent {
  id: number;
}

@Component({
  selector: 'app-rig-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './rig-detail.component.html',
  styleUrl: './rig-detail.component.scss'
})
export class RigDetailComponent implements OnChanges, OnDestroy {
  @Input() rig: Rig | null = null;
  @Input() businessUnits: LookupDto[] = [];

  @Output() saved = new EventEmitter<RigSavedEvent>();

  private readonly rigsService = inject(RigsService);
  private readonly fb = inject(FormBuilder);
  private saveRequest?: Subscription;

  protected isSaving = signal(false);
  protected errorMessage = signal('');

  protected form = this.fb.group({
    name:                  [null as string | null, [Validators.required, Validators.maxLength(200)]],
    alias:                 [null as string | null, Validators.maxLength(50)],
    defaultBusinessUnitId: [null as number | null]
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['rig']) return;

    if (this.rig) {
      this.form.reset();
      this.form.patchValue({
        name:                  this.rig.name ?? '',
        alias:                 this.rig.alias,
        defaultBusinessUnitId: this.rig.defaultBusinessUnitId
      }, { emitEvent: false });
    } else {
      this.form.reset();
    }

    this.errorMessage.set('');
    this.form.enable();
  }

  ngOnDestroy(): void {
    this.saveRequest?.unsubscribe();
  }

  get isNew(): boolean {
    return !this.rig || this.rig.id === 0;
  }

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  protected onSave(): void {
    if (this.form.invalid || this.isSaving()) return;

    const raw = this.form.getRawValue();
    const dto: SaveRigDto = {
      name:                  raw.name!.trim(),
      alias:                 raw.alias?.trim() ?? null,
      defaultBusinessUnitId: raw.defaultBusinessUnitId
    };

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.clearServerErrors();

    const request$ = this.isNew
      ? this.rigsService.create(dto)
      : this.rigsService.update(this.rig!.id, dto);

    this.saveRequest = request$.subscribe({
      next: savedRig => {
        this.isSaving.set(false);
        this.saved.emit({ id: savedRig.id });
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.handleServerError(err);
      }
    });
  }

  private handleServerError(err: HttpErrorResponse): void {
    const body = err.error;

    // Map field-level errors from ValidationProblem response onto form controls.
    if (body?.errors && typeof body.errors === 'object') {
      const errors = body.errors as Record<string, string[]>;

      let mapped = false;
      for (const [serverField, messages] of Object.entries(errors)) {
        const control = this.form.get(serverField.toLowerCase());
        if (control) {
          control.setErrors({ serverError: messages[0] });
          control.markAsTouched();
          mapped = true;
        }
      }

      if (!mapped) {
        this.errorMessage.set('Save failed. Please check your entries and try again.');
      }
    } else {
      this.errorMessage.set('An unexpected error occurred. Please try again.');
    }
  }

  private clearServerErrors(): void {
    for (const key of Object.keys(this.form.controls)) {
      const control = this.form.get(key);
      if (control?.hasError('serverError')) {
        const { serverError: _, ...rest } = control.errors ?? {};
        control.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }
    }
  }
}
